import { ErrorBoundary, injectStores, LayoutRouter } from "@intrasync/react";
import React from 'react';
import { useMediaQuery, useTheme } from "@mui/material";
import type { RouterStore } from "@intrasync/stores";
import useVisualViewport from "hooks/useVisualViewport";

/**
 * Drives `routerStore.forceFullScreenDialog` from the viewport: on phones every
 * dialog opens full-screen (the library Dialog reads this flag) instead of a
 * cramped centered card with ~32px margins each side. Renders nothing.
 *
 * H10 · The cutoff is `md` (900), not `sm` (600). At `sm` an iPad in portrait
 * (820 CSS px) counted as desktop and got centred cards — including dialogs
 * with a hard-coded `height: 620` and no `maxWidth`, which resolve to `xl` and
 * therefore to a 620px-tall card floating in a 1180px viewport with its
 * actions below the fold. Registry Connections, Portainer Connections and the
 * Client Instances quartet all land there. Touch input, not screen width, is
 * what makes a full-screen dialog the right shape, and touch runs to 900.
 */
const ResponsiveDialogSync = injectStores(
  ({ routerStore }: { routerStore?: RouterStore }) => {
    const theme = useTheme();
    const isTouch = useMediaQuery(theme.breakpoints.down("md"));
    React.useEffect(() => {
      routerStore?.setForceFullScreenDialog?.(isTouch);
    }, [isTouch, routerStore]);
    return null;
  }, {}
);

const App: React.FC = () => {
  // H12 · Publishes --vvh / --keyboard-inset for the whole app. Mounted here
  // rather than per-view so a page that pins a composer or a footer can just
  // read the variable; there is no document scroll to fall back on.
  useVisualViewport();

  return (
    <>
      <ResponsiveDialogSync />
      <ErrorBoundary isLayout name="LayoutRouter">
        <LayoutRouter />
      </ErrorBoundary>
    </>
  );
};

export default App;
